/**
 * Market panel — stock indices, commodities, crypto prices.
 * Displays sparkline charts, change indicators, volume bars.
 * Data from market-source (CoinGecko + Finnhub).
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { getLatestMarketQuotes } from '@/sources/built-in/market-source';
import { createSparkline } from '@/ui/components/sparkline';

type FilterCategory = 'all' | 'index' | 'commodity' | 'crypto';

export function createMarketPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'market-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;
  let activeFilter: FilterCategory = 'all';

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function render(): void {
    const quotes = getLatestMarketQuotes();
    contentArea.textContent = '';

    // Filter bar
    const filterBar = document.createElement('div');
    filterBar.style.cssText = 'display: flex; gap: 4px; margin-bottom: 12px; flex-wrap: wrap;';

    const filters: { label: string; value: FilterCategory }[] = [
      { label: 'All', value: 'all' },
      { label: 'Indices', value: 'index' },
      { label: 'Commodities', value: 'commodity' },
      { label: 'Crypto', value: 'crypto' },
    ];

    for (const f of filters) {
      const btn = document.createElement('button');
      btn.style.cssText = `
        padding: 3px 10px; border-radius: 12px; border: 1px solid var(--border-primary);
        font-size: 10px; cursor: pointer; transition: all 150ms;
        background: ${activeFilter === f.value ? 'var(--accent-blue)' : 'var(--bg-secondary)'};
        color: ${activeFilter === f.value ? '#fff' : 'var(--text-secondary)'};
      `;
      btn.textContent = f.label;
      btn.onclick = () => {
        activeFilter = f.value;
        scheduleRender();
      };
      filterBar.appendChild(btn);
    }

    contentArea.appendChild(filterBar);

    // Filter quotes
    const filtered = activeFilter === 'all'
      ? quotes
      : quotes.filter(q => q.category === activeFilter);

    if (filtered.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 24px 0; text-align: center;';
      msg.textContent = quotes.length === 0
        ? 'Waiting for market data\u2026'
        : 'No assets in this category';
      contentArea.appendChild(msg);
      return;
    }

    // Group by category
    const groups = new Map<string, typeof filtered>();
    for (const q of filtered) {
      const arr = groups.get(q.category) ?? [];
      arr.push(q);
      groups.set(q.category, arr);
    }

    const categoryLabels: Record<string, string> = {
      index: 'Stock Indices',
      commodity: 'Commodities',
      crypto: 'Crypto',
      forex: 'Forex',
    };

    for (const [category, items] of groups) {
      // Section header
      const header = document.createElement('div');
      header.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; margin-top: 8px; text-transform: uppercase; letter-spacing: 0.05em;';
      header.textContent = categoryLabels[category] ?? category;
      contentArea.appendChild(header);

      for (const q of items) {
        contentArea.appendChild(renderQuoteRow(q));
      }
    }
  }

  interface QuoteData {
    symbol: string;
    name: string;
    price: number;
    change24h: number;
    volume24h?: number;
    high24h?: number;
    low24h?: number;
    sparkline?: number[];
  }

  function renderQuoteRow(q: QuoteData): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: center; gap: 8px;
      padding: 6px 8px; margin-bottom: 3px; border-radius: 4px;
      background: var(--bg-secondary);
    `;

    // Symbol + Name column
    const nameCol = document.createElement('div');
    nameCol.style.cssText = 'flex: 1; min-width: 0;';

    const symEl = document.createElement('div');
    symEl.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary); font-family: var(--font-mono);';
    symEl.textContent = q.symbol;
    nameCol.appendChild(symEl);

    const nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-size: 10px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
    nameEl.textContent = q.name;
    nameCol.appendChild(nameEl);

    row.appendChild(nameCol);

    // Sparkline (if available)
    if (q.sparkline && q.sparkline.length >= 2) {
      const sparkContainer = document.createElement('div');
      sparkContainer.style.cssText = 'flex-shrink: 0;';

      const color = q.change24h >= 0 ? 'var(--accent-green)' : 'var(--accent-red)';
      const spark = createSparkline({
        data: q.sparkline.length > 48 ? downsample(q.sparkline, 48) : q.sparkline,
        width: 60,
        height: 20,
        color,
        fill: true,
        fillOpacity: 0.1,
        strokeWidth: 1,
      });
      sparkContainer.appendChild(spark);
      row.appendChild(sparkContainer);
    }

    // Price + Change column
    const priceCol = document.createElement('div');
    priceCol.style.cssText = 'text-align: right; flex-shrink: 0; min-width: 70px;';

    const priceEl = document.createElement('div');
    priceEl.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary); font-family: var(--font-mono);';
    priceEl.textContent = `$${formatPrice(q.price)}`;
    priceCol.appendChild(priceEl);

    const changeEl = document.createElement('div');
    const isPositive = q.change24h >= 0;
    changeEl.style.cssText = `
      font-size: 10px; font-weight: 500; font-family: var(--font-mono);
      color: ${isPositive ? 'var(--accent-green)' : 'var(--accent-red)'};
    `;
    const arrow = isPositive ? '\u25B2' : '\u25BC';
    changeEl.textContent = `${arrow} ${Math.abs(q.change24h).toFixed(2)}%`;
    priceCol.appendChild(changeEl);

    row.appendChild(priceCol);

    return row;
  }

  // Initial render
  render();

  // Re-render when events change (market source emits events for big moves)
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  // Also refresh on analysis cycle (market data updates cached quotes)
  cleanupFns.push(
    eventBus.on('analysis:complete', () => scheduleRender()),
  );

  // Periodic refresh to pick up cached quote updates (every 60s)
  const interval = setInterval(() => scheduleRender(), 60_000);
  cleanupFns.push(() => clearInterval(interval));

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}

/** Downsample an array to targetLen points using simple bucket averaging. */
function downsample(data: number[], targetLen: number): number[] {
  if (data.length <= targetLen) return data;
  const bucketSize = data.length / targetLen;
  const result: number[] = [];
  for (let i = 0; i < targetLen; i++) {
    const start = Math.floor(i * bucketSize);
    const end = Math.floor((i + 1) * bucketSize);
    let sum = 0;
    for (let j = start; j < end; j++) sum += data[j];
    result.push(sum / (end - start));
  }
  return result;
}

/** Format price with appropriate decimal places. */
function formatPrice(price: number): string {
  if (price >= 1000) return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (price >= 1) return price.toFixed(2);
  return price.toFixed(4);
}
